import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { Loader2, Home, Briefcase, CreditCard } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button, cn } from '@scio3/ui';
import { GET_APP_CFG } from '@scio3/graphql-client';
import { PLAN_FEATURES, BILLING_INTERVAL } from '@scio3/shared-types';
import { useSubscriptionWizard, STEP } from '../hooks/useSubscriptionWizard';
import type { WizardStep } from '../hooks/useSubscriptionWizard';
import { getPlanDisplayName, getPlanUpgradePitchKey } from '../utils/plan-helpers';
import { useTeamStore } from '@/stores/team-store';
import { ProcessingOverlay } from '../components/ProcessingOverlay';
import { ScreenManagementDialog } from './ScreenManagementDialog';
import { ChangePlanWarningDialog } from './wizard/ChangePlanWarningDialog';
import { TeamPlanBlockDialog } from './wizard/TeamPlanBlockDialog';
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';
import { WizardStepScreens } from './wizard/WizardStepScreens';
import { WizardStepPlan } from './wizard/WizardStepPlan';
import { WizardStepConfirmation } from './wizard/WizardStepConfirmation';
import { WizardStepFinish } from './wizard/WizardStepFinish';

// ---------------------------------------------------------------------------
// Step indicator config
// ---------------------------------------------------------------------------

/** Step indicators are only rendered for trial users, who follow the
 *  PLAN → NUMBER_SCREEN → CONFIRMATION flow (plan-first). */
const STEP_INDICATORS = [
  { step: STEP.PLAN, icon: Briefcase, label: 'Select Plan' },
  { step: STEP.NUMBER_SCREEN, icon: Home, label: 'Screen Number' },
  { step: STEP.CONFIRMATION, icon: CreditCard, label: 'Payment' },
] as const;

/** Map wizard step to step indicator index (0-2) */
function getIndicatorIndex(step: WizardStep): number {
  switch (step) {
    case STEP.PLAN:
      return 0;
    case STEP.NUMBER_SCREEN:
      return 1;
    case STEP.CONFIRMATION:
      return 2;
    case STEP.FINISH:
      return 3;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SubscriptionWizardDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const w = useSubscriptionWizard();
  const activeIndicatorIndex = getIndicatorIndex(w.step);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Load AppCfg to check if TypeForm cancel subscription is configured
  const { data: appCfgData } = useQuery<{ getAppCfgs: Record<string, unknown> }>(GET_APP_CFG, {
    variables: { teamId: activeTeamId, appType: 'scio' },
    skip: !activeTeamId || !w.isOpen,
    fetchPolicy: 'cache-and-network',
  });
  const typeFormCancelEmbed = appCfgData?.getAppCfgs?.typeFormCancelSubscriptionEmbed as
    | string
    | undefined;

  // Show "Cancel Subscription" button — scio1 conditions:
  // 1. TypeForm embed configured, 2. has screens, 3. not trial, 4. not free plan,
  // 5. not team billing (team-billing cancel = reduce screens to 0, no survey).
  //
  // QC #1310 — sub-account DOES show the Cancel button in scio1 (mirrors parent
  // account behavior — TypeForm survey + Cancel mutation accepts subAccountId).
  // Previous gate `!w.contextSubAccountId` was wrong; removed.
  const showCancelButton =
    !w.contextTeamId &&
    !!typeFormCancelEmbed &&
    (w.maxScreens ?? 0) > 0 &&
    !w.isTrial &&
    w.currentPlanFeature !== PLAN_FEATURES.FREE_PLAN;

  // #1113 — when a paid user reduces screens to 0 and clicks Update, route
  // through the TypeForm cancel-survey flow instead of the regular paid-update
  // path. Same effect as clicking Cancel Subscription. Matches scio1
  // (subscriptionPlanModal.js:2054).
  const shouldRouteToCancelOnUpdate = showCancelButton && w.screenCount === 0;

  // ---- Render step content ----
  const renderStepContent = () => {
    switch (w.step) {
      case STEP.NUMBER_SCREEN:
        return (
          <WizardStepScreens
            screenCount={w.screenCount}
            onScreenCountChange={w.setScreenCount}
            minScreens={w.minScreens}
            planInfoText={w.planInfoText}
            isPaid={!w.isTrial}
            planName={w.aliasData?.name ?? getPlanDisplayName(w.currentPlanFeature ?? 'free')}
            planPriceText={`${w.currencySymbol}${w.currentPricePerScreen.toFixed(2)}/screen/${w.billingInterval === BILLING_INTERVAL.YEARLY ? t('per year') : t('per month')}`}
            onChangePlan={w.skipPlanStep ? undefined : () => w.setStep(STEP.PLAN)}
            // Only surface Screen Management when there are paired screens
            // to unpair. With 0 paired screens, hitting min=1 should just
            // disable the - button — opening a "remove screens to reduce"
            // dialog with "No devices found" is confusing.
            //
            // GitLab #1324 — when parent-managing-sub-account context is
            // active (`contextSubAccountId` set), don't open Screen Management
            // either: the backend cross-account check
            // (device.service.ts: `device.accountId !== authContext.accountId`)
            // rejects parent's unpair attempt on sub-account screens. scio1
            // parity: parent simply cannot reduce below the sub-account's
            // paired count — `-` stays silently disabled at the floor.
            onDecreaseBlocked={
              w.usedScreens > 0 && !w.contextSubAccountId
                ? () => w.setShowScreenManagement(true)
                : undefined
            }
            teamMode={!!w.contextTeamId}
            teamName={w.teamNameInput}
            onTeamNameChange={w.setTeamNameInput}
            teamDescription={w.teamDescriptionInput}
            onTeamDescriptionChange={w.setTeamDescriptionInput}
          />
        );

      case STEP.PLAN:
        return (
          <WizardStepPlan
            selectedPlan={w.selectedPlan}
            onPlanChange={w.handleSelectPlan}
            onSelectFree={w.handleSelectFree}
            selectingFree={w.isSelectingFree}
            // Pass the REAL current plan (no pro→proPlus normalization). The retired `pro`
            // card is hidden (isActive=false), so this won't highlight any card for a legacy
            // pro account — and crucially it does NOT mark the Pro Plus card as "Current Plan"
            // (which would make it unselectable and re-block the upgrade).
            currentPlan={w.currentPlanFeature ?? undefined}
            currency={w.currency}
            onCurrencyChange={w.setCurrency}
            currencyDisabled={!w.isTrial}
            minPlanRequired={w.contextMinPlan}
            // SP45 — hide the Free row when the user's effective plan is
            // already Free (single source of truth lives in useSubscriptionWizard).
            // Expired-trial accounts carry `isTrial=true` but planFeature=`'free'`,
            // so they still hide. Pro Plus trial users keep it visible.
            hideFreePlanOption={w.isOnFreePlan}
            billingInterval={w.billingInterval}
            onBillingIntervalChange={w.setBillingInterval}
            // #1134 — On Upgrade Required paywall, lock the Monthly/Yearly toggle
            // when the customer is already on Yearly so they can't downgrade through
            // the paywall. Trial users are excluded (free first-time choice).
            lockBillingToggle={
              !!w.contextMinPlan &&
              !w.isTrial &&
              (w.currentBillingInterval === BILLING_INTERVAL.YEARLY ||
                w.currentBillingInterval === BILLING_INTERVAL.YEARLY_FREEDEVICE)
            }
          />
        );

      case STEP.CONFIRMATION:
        return (
          <WizardStepConfirmation
            isTrial={w.isTrial}
            // #1411 — free-plan upgraders have no active subscription to prorate,
            // so "Due Today" must mirror the Subtotal (the amount actually charged).
            isFreePlan={w.isOnFreePlan}
            // #1413 — a cancelled/inactive account re-subscribing is the same case:
            // no active subscription → backend review returns total=0 even though the
            // card is charged the full amount. Treat like trial/free for "Due Today".
            isAccountInactive={w.isAccountInactive}
            existingCard={w.existingCard}
            useExistingCard={w.useExistingCard}
            onUseExistingCardChange={w.setUseExistingCard}
            stripeLoading={w.stripeLoading}
            stripeClientSecret={w.stripeClientSecret}
            stripePaymentMethodOrder={w.stripePaymentMethodOrder}
            stripeReturnUrl={w.stripeReturnUrl}
            defaultEmail={w.defaultEmail}
            onStripeSuccess={w.handleStripeSuccess}
            isSubmitting={w.isSubmitting}
            billingInterval={w.billingInterval}
            currentPricePerScreen={w.currentPricePerScreen}
            screenCount={w.screenCount}
            onScreenCountChange={w.setScreenCount}
            minScreens={w.minScreens}
            originalScreenCount={w.maxScreens}
            disableScreenCount={!!w.contextMinPlan}
            addonTotal={w.addonTotal}
            addonItems={w.addonItems}
            currency={w.currency}
            selectedPlan={w.selectedPlan}
            planSummaryName={getPlanDisplayName(w.submitPlanAlias)}
            planEditName={
              w.aliasPlanData?.[w.currency]?.[w.billingInterval]?.name as string | undefined
            }
            planEditDiscount={
              w.aliasPlanData?.[w.currency]?.[w.billingInterval]?.discount as number | undefined
            }
            reviewResult={w.reviewResult}
            reviewLoading={w.reviewLoading}
            initialCredit={w.initialCredit}
            onConfirmExistingCard={w.handleConfirmExistingCard}
            paypalLoading={w.paypalLoading}
            onPaypalCheckout={w.handlePaypalCheckout}
            promoCode={w.promoCode}
            onPromoCodeChange={w.setPromoCode}
            showPromoCode={w.showPromoCode}
            onShowPromoCode={w.setShowPromoCode}
            onBillingAddressChange={w.handleBillingAddressChange}
            onStripeSubmitStart={() => w.setStripeProcessing(true)}
            onStripeSubmitError={() => w.setStripeProcessing(false)}
            teamMode={!!w.contextTeamId}
            editedDeviceFrees={w.editedDeviceFrees}
            onInitDeviceFrees={w.initEditedDeviceFrees}
            onBundleQuantityChange={w.setBundleQuantity}
          />
        );

      case STEP.FINISH:
        return (
          <WizardStepFinish
            chargeResult={w.chargeResult as Parameters<typeof WizardStepFinish>[0]['chargeResult']}
            currency={w.currency}
            currencySymbol={w.currencySymbol}
            currentCard={w.existingCard}
            onClose={() => w.handleOpenChange(false)}
          />
        );
    }
  };

  return (
    <Dialog open={w.isOpen} onOpenChange={w.handleOpenChange}>
      <DialogContent
        className={cn(
          'w-[calc(100vw-2rem)] flex flex-col overflow-hidden sm:max-w-6xl p-0 bg-muted',
          // Trial users see an extra step indicator strip + plan-step copy, so give the
          // dialog more vertical room to fit everything without forcing inner scroll.
          w.isTrial ? 'max-h-[95vh]' : 'max-h-[85vh]',
        )}
        data-testid="subscription-wizard-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-6 pt-5 pb-4 space-y-0.5 bg-transparent">
          <DialogTitle className="text-xl font-semibold">
            {/* Caller-provided title (e.g. "Select Your Plan" from the trial banner)
                wins over the default ternary so proactive entry points get friendlier copy. */}
            {w.contextTitle ??
              (w.contextMinPlan ? t('Upgrade Required') : t('Update Subscription Plan'))}
          </DialogTitle>
          {w.step === STEP.PLAN && !w.contextMinPlan && !w.isTrial && (
            <p className="text-sm text-muted-foreground">
              {t('You are currently on the {{plan}} plan', {
                plan: getPlanDisplayName(w.currentPlanFeature ?? 'free'),
              })}
            </p>
          )}
          {w.contextMinPlan && w.step === STEP.PLAN && (
            <p className="text-sm text-muted-foreground">
              {/* Trial-banner upgrade path provides its own friendlier title via
                  `wizardTitle`. In that proactive trial-conversion context, swap the
                  feature-gate copy for trial-conversion copy. Feature-gate callers (no
                  wizardTitle) get a benefit-led, plan-specific pitch that highlights
                  what the upgrade unlocks (vs. just the price). */}
              {w.contextTitle
                ? t('Choose a plan to keep your screens running after your trial ends.')
                : t(getPlanUpgradePitchKey(w.contextMinPlan), {
                    plan: w.aliasData?.name ?? getPlanDisplayName(w.contextMinPlan),
                  })}
            </p>
          )}
        </DialogHeader>

        {/* Step indicator — centered on its own row below the header. Hidden on FINISH
            and for paid users. */}
        {w.step !== STEP.FINISH && w.isTrial && (
          <div className="px-6 pt-1 pb-2">
            {/* scio1-parity progress bar: each step renders [left line | circle | right line]
                with the lines flex-growing inside an equal-width column. Adjacent steps' line
                halves butt together to form a single continuous bar that passes flush against
                each circle's edge, exactly like scio1's wizard.
                Color rule: the segment between step N and N+1 turns green once the user has
                moved past step N (activeIndicatorIndex > N). The line entering step 0 is
                always green so the user gets a "you've started the flow" cue. */}
            <div className="flex items-start justify-center max-w-md mx-auto">
              {STEP_INDICATORS.map((indicator, idx) => {
                const Icon = indicator.icon;
                const isDone = activeIndicatorIndex > idx;
                const isCurrent = activeIndicatorIndex === idx;
                const isDisabled = activeIndicatorIndex < idx;
                const isFirst = idx === 0;
                const isLast = idx === STEP_INDICATORS.length - 1;
                // Left half of segment idx-1 → idx: green once user has reached idx.
                const leftLineColor = activeIndicatorIndex >= idx ? 'bg-primary' : 'bg-slate-300';
                // Right half of segment idx → idx+1: green once user has moved past idx.
                const rightLineColor = activeIndicatorIndex > idx ? 'bg-primary' : 'bg-slate-300';

                return (
                  <div key={indicator.step} className="flex flex-col items-center flex-1 min-w-0">
                    <div className="flex items-center w-full">
                      {/* Left half of the connector — hidden on the first step */}
                      <div className={cn('h-1 flex-1', isFirst ? 'invisible' : leftLineColor)} />
                      <div
                        className={cn(
                          'flex items-center justify-center rounded-full border-2 h-10 w-10 shrink-0',
                          isCurrent || isDone
                            ? 'border-primary bg-primary text-white'
                            : 'border-slate-300 bg-card text-muted-foreground',
                        )}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      {/* Right half of the connector — hidden on the last step */}
                      <div className={cn('h-1 flex-1', isLast ? 'invisible' : rightLineColor)} />
                    </div>
                    <span
                      className={cn(
                        'mt-1 text-xs whitespace-nowrap',
                        isCurrent
                          ? 'text-primary font-bold'
                          : isDisabled
                            ? 'text-muted-foreground'
                            : 'text-foreground',
                      )}
                    >
                      {t(indicator.label)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <ProcessingOverlay visible={w.isSubmitting} />

        {/* Step content — show loader while sub-account data loads.
            pb-24 reserves space below the content so it never tucks under the floating footer. */}
        <div className="flex-1 overflow-y-auto px-10 pt-4 pb-24">
          {w.loadingSubAccountData ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            renderStepContent()
          )}
        </div>

        {/* Footer with navigation — floats over the scroll area with a soft upward shadow */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-6 py-4 bg-card shadow-[0_-4px_16px_rgba(0,0,0,0.06)] z-10">
          <div className="flex items-center gap-2">
            {showCancelButton && w.step === STEP.NUMBER_SCREEN && (
              <Button
                variant="outline"
                className="cursor-pointer text-destructive border-destructive hover:bg-destructive/10"
                onClick={() => setShowCancelDialog(true)}
                data-testid="wizard-cancel-subscription-btn"
              >
                {t('Cancel Subscription')}
              </Button>
            )}
            {w.stepIndex > 0 &&
              w.step !== STEP.FINISH &&
              !(w.contextMinPlan && w.step === STEP.PLAN) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!w.isTrial && w.step === STEP.CONFIRMATION) {
                      if (w.skippedToConfirmation) {
                        w.setSkippedToConfirmation(false);
                        w.setStep(STEP.NUMBER_SCREEN);
                      } else {
                        w.setStep(STEP.PLAN);
                      }
                    } else {
                      w.goBack();
                    }
                  }}
                  className="cursor-pointer"
                  data-testid="wizard-prev-btn"
                >
                  {t('Previous')}
                </Button>
              )}
          </div>
          <div className="flex items-center gap-2">
            {/* Footer Update Subscription button on CONFIRMATION step.
                The Summary sidebar inside WizardStepConfirmation owns this button
                in the paid layout (#1145), but the *trial* layout has no sidebar.
                Render the footer button only when the sidebar won't — i.e. when
                `isShowConfirm = !isTrial || !!existingCard` is FALSE: trial user
                with no card on file. Covers BOTH the fresh-trial-signup flow
                AND the Trial→Free upgrade-required flow (#1181 — `payment.isTrial`
                lingers `true` after downgrade, so the wizard renders the trial
                layout there too). Without this, those users hit a dead end on
                the Payment step (regression of #1145). */}
            {w.step === STEP.FINISH ? null : w.step === STEP.CONFIRMATION ? (
              w.isTrial && !w.existingCard ? (
                w.useExistingCard ? (
                  <Button
                    onClick={w.handleConfirmExistingCard}
                    disabled={w.isSubmitting || w.reviewLoading}
                    className={cn(
                      w.isSubmitting || w.reviewLoading
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer',
                    )}
                    data-testid="wizard-submit-btn"
                  >
                    {w.isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('Processing...')}
                      </>
                    ) : (
                      t('Update subscription')
                    )}
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    form="stripe-payment-form"
                    disabled={w.isSubmitting || w.stripeProcessing || !w.stripeClientSecret}
                    className={cn(
                      w.isSubmitting || w.stripeProcessing || !w.stripeClientSecret
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer',
                    )}
                    data-testid="wizard-submit-btn"
                  >
                    {w.isSubmitting || w.stripeProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('Processing...')}
                      </>
                    ) : (
                      t('Update subscription')
                    )}
                  </Button>
                )
              ) : null
            ) : !w.isTrial && w.step === STEP.NUMBER_SCREEN ? (
              <Button
                onClick={() => {
                  if (shouldRouteToCancelOnUpdate) {
                    setShowCancelDialog(true);
                  } else {
                    w.handlePaidUpdate();
                  }
                }}
                disabled={!w.canGoNext || !w.planFeatureId}
                className={cn(
                  !w.canGoNext || !w.planFeatureId
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer',
                )}
                data-testid="wizard-update-btn"
              >
                {t('Update')}
              </Button>
            ) : w.contextMinPlan && w.step === STEP.PLAN ? (
              <Button
                onClick={() => w.goNext()}
                disabled={
                  !w.canGoNext ||
                  w.validatingPlan ||
                  (!w.isTrial && w.selectedPlan === w.currentPlanFeature)
                }
                className={cn(
                  !w.canGoNext ||
                    w.validatingPlan ||
                    (!w.isTrial && w.selectedPlan === w.currentPlanFeature)
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer',
                )}
                data-testid="wizard-change-btn"
              >
                {w.validatingPlan ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('Checking...')}
                  </>
                ) : (
                  t('Change')
                )}
              </Button>
            ) : (
              <Button
                onClick={() => w.goNext()}
                disabled={!w.canGoNext || w.validatingPlan}
                className={cn(
                  !w.canGoNext || w.validatingPlan
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer',
                )}
                data-testid="wizard-next-btn"
              >
                {w.validatingPlan ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('Checking...')}
                  </>
                ) : (
                  t('Next')
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Screen Management dialog — opens when user tries to decrease below min */}
      <ScreenManagementDialog
        open={w.showScreenManagement}
        onClose={() => w.setShowScreenManagement(false)}
        totalPairedScreens={w.minScreens}
        subAccountId={w.contextSubAccountId}
        teamId={w.contextTeamId}
      />

      {/* Plan change warning dialog */}
      <ChangePlanWarningDialog
        open={w.showChangePlanWarning}
        warnings={w.changePlanWarnings}
        confirmLabel={w.changePlanWarningConfirmLabel}
        onDismiss={w.dismissChangePlanWarning}
        onConfirm={w.confirmChangePlanWarning}
      />

      {/* #1275 — block downgrading to a no-Team plan (Free/Standard) from inside a non-default team */}
      <TeamPlanBlockDialog
        open={w.teamPlanBlockOpen}
        onDismiss={w.dismissTeamPlanBlock}
        onSwitchToDefault={w.switchToDefaultTeam}
      />

      {/* TypeForm cancel subscription dialog — fires `cancelSubscription`
          mutation + refetches GetCompanyInfo internally (#1113), so by the time
          this onClose runs the cache is already fresh. We only need to close
          the wizard; no setTimeout, no duplicate refetch. */}
      <CancelSubscriptionDialog
        open={showCancelDialog}
        onClose={(submitted) => {
          setShowCancelDialog(false);
          if (submitted) {
            w.handleOpenChange(false);
          }
        }}
        subAccountId={w.contextSubAccountId}
        onSwitchPlans={() => {
          setShowCancelDialog(false);
          // Use the cancel-flow handoff (not raw setStep) so picking Free
          // from the PLAN step skips the redundant DowngradeSurveyDialog.
          w.enterPlanStepFromCancelFlow();
        }}
        onSwitchToStandard={(targetPlanAlias) => {
          setShowCancelDialog(false);
          // Target is dynamic: Engage's retention offer is Pro Plus, not Standard.
          // Forward whatever the retention card resolved to instead of hardcoding.
          w.switchPlanFromCancelFlow(targetPlanAlias);
        }}
        onSwitchToFree={() => {
          // Same path as the plan-step "Select Free" button — closes the cancel
          // dialog and opens DowngradeSurveyDialog (then DowngradeComparisonDialog).
          setShowCancelDialog(false);
          w.handleSelectFree();
        }}
        onKeepSubscription={() => {
          // Tear down both popups (cancel dialog + wizard) and return the user
          // to the Subscription page they came from.
          setShowCancelDialog(false);
          w.handleOpenChange(false);
          navigate('/app/s/subscription-plan');
        }}
      />
    </Dialog>
  );
}
